/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */

import { useState, useEffect, useMemo } from "react";
import { OTHER_PREFIX_VALUE, PREFIX_OPTIONS } from "constants/prefixes";
import { getUIPrefix } from "utils/prefix";
import decrypt from "utils/decrypt";
import { getFieldDisplayLabel } from "./fieldLabelUtils";
import isValidEmail from "utils/isValidEmail";
import isValidUrl, { normalizeUrl } from "utils/isValidUrl";

export interface DynamicFieldOption {
  id: number | string;
  label: string;
  value: string;
}

export interface DynamicField {
  type:
    | "number"
    | "toggle"
    | "email"
    | "text_or_document"
    | "prefix"
    | "single_select"
    | "multi_select"
    | "checkbox"
    | "select"
    | "phone_number"
    | "date"
    | "terms_and_conditions"
    | "radio_button_select"
    | "dropdown_list"
    | "text"
    | "long_text"
    | "single_line_text"
    | "textarea"
    | "link";
  is_visible: boolean;
  is_custom: boolean;
  required?: boolean;
  is_required?: boolean;
  label: string;
  description?: string;
  key: string | { code: string; number: string }; // Support object keys for phone number fields
  order?: number;
  options?: DynamicFieldOption[] | string[];
  renderAsPrefix?: boolean; // When true, render text field as PrefixSelect
  optionsKey?: string; // Key to reference predefined options
  placeholder?: string;
  formats?: string[];
  maxSize?: number;
  validation?: {
    min?: number;
    max?: number;
    minLength?: number;
    maxLength?: number;
    pattern?: string;
  };
  dependent_on?: string; // For conditional fields
  dependent_value?: string | string[];
}

export interface DynamicSection {
  title: string;
  fields: Record<string, DynamicField>;
}

export interface Sections {
  [key: string]: DynamicSection;
}

export interface DynamicFieldValues {
  [key: string]: string | string[];
}

export interface DynamicFieldErrors {
  [key: string]: string;
}

const useDynamicFields = (
  sections: Sections,
  companion?: any,
  isCompanion?: boolean,
) => {
  const [dynamicFieldValues, setDynamicFieldValues] =
    useState<DynamicFieldValues>({});
  const [dynamicFieldErrors, setDynamicFieldErrors] =
    useState<DynamicFieldErrors>({});

  // Get all dynamic fields (both custom and non-custom)
  const dynamicFields = useMemo(() => {
    const fields: Record<string, DynamicField> = {};

    Object.values(sections).forEach((section) => {
      if (section?.fields) {
        Object.entries(section.fields).forEach(([fieldKey, field]) => {
          fields[fieldKey] = field;
        });
      }
    });

    return fields;
  }, [sections]);

  const hasDynamicFields = useMemo(() => {
    return Object.keys(dynamicFields).length > 0;
  }, [dynamicFields]);

  useEffect(() => {
    const initialValues: DynamicFieldValues = {};
    Object.entries(dynamicFields).forEach(([fieldKey, field]) => {
      if (field.type === "multi_select") {
        initialValues[fieldKey] = [];
      } else {
        initialValues[fieldKey] = "";
      }
    });
    setDynamicFieldValues(initialValues);
  }, [dynamicFields]);

  // Populate dynamic fields from companion data
  useEffect(() => {
    if (companion && Object.keys(dynamicFields).length > 0) {
      const populatedValues: DynamicFieldValues = {};

      Object.entries(dynamicFields).forEach(([fieldKey, field]) => {
        // Handle phone number fields with object keys
        if (
          field.type === "phone_number" &&
          typeof field.key === "object" &&
          field.key.code &&
          field.key.number
        ) {
          const phoneNumber = companion[field.key.number] || "";
          // Store only the phone number part; country code is handled separately by the component
          // This works for both assistant and emergency contact phone numbers
          populatedValues[fieldKey] = phoneNumber;
          return;
        }

        // For string keys only
        if (typeof field.key !== "string") {
          populatedValues[fieldKey] = "";
          return;
        }

        const dataValue = companion[field.key] || companion[fieldKey];

        // Handle document_number fields with decryption
        if (field.key === "document_number" || fieldKey === "document_number") {
          const decryptedValue = dataValue ? decrypt(dataValue) : "";
          populatedValues[fieldKey] = decryptedValue;
          return;
        }

        // Handle prefix field
        if (field.key === "prefix" || fieldKey === "prefix") {
          if (dataValue) {
            const result = getUIPrefix(dataValue, PREFIX_OPTIONS);
            populatedValues[fieldKey] =
              result && result.selected ? result.selected : dataValue;
          } else {
            populatedValues[fieldKey] = "";
          }
          return;
        }

        // Handle select-type fields that need value-to-ID mapping
        if (
          ["single_select", "multi_select", "dropdown_list"].includes(
            field.type,
          ) &&
          field.options
        ) {
          const normalizedOptions = field.options.map((option, index) => {
            if (typeof option === "string") {
              return { id: index.toString(), label: option, value: option };
            }
            return {
              id: (option.id ?? index).toString(),
              label: option.label,
              value: option.value ?? option.label,
            };
          });

          if (field.type === "multi_select") {
            let dataValues: string[] = [];

            if (typeof dataValue === "string" && dataValue) {
              // Handle || separated values from backend
              dataValues = dataValue
                .split(/[|]+/)
                .map((v) => v.trim())
                .filter(Boolean);
            } else if (Array.isArray(dataValue)) {
              dataValues = dataValue;
            }

            // Map backend values to option IDs
            const selectedIds = dataValues
              .map((value) => {
                const found = normalizedOptions.find(
                  (opt) => opt.value === value || opt.label === value,
                );
                return found ? found.id : null;
              })
              .filter(Boolean) as string[];

            populatedValues[fieldKey] = selectedIds;
          } else {
            // single_select or dropdown_list
            if (dataValue) {
              const found = normalizedOptions.find(
                (opt) => opt.value === dataValue || opt.label === dataValue,
              );
              populatedValues[fieldKey] = found ? found.id : "";
            } else {
              populatedValues[fieldKey] = "";
            }
          }
        } else {
          // Text fields - use value directly
          populatedValues[fieldKey] = dataValue || "";
        }
      });

      setDynamicFieldValues((prev) => ({
        ...prev,
        ...populatedValues,
      }));
    }
  }, [companion, dynamicFields]);

  const updateDynamicField = (fieldKey: string, value: string | string[]) => {
    setDynamicFieldValues((prev) => {
      const newValues = {
        ...prev,
        [fieldKey]: value,
      };
      return newValues;
    });

    if (dynamicFieldErrors[fieldKey]) {
      setDynamicFieldErrors((prev) => ({
        ...prev,
        [fieldKey]: "",
      }));
    }
  };

  const setDynamicFieldError = (fieldKey: string, error: string) => {
    setDynamicFieldErrors((prev) => ({
      ...prev,
      [fieldKey]: error,
    }));
  };

  const clearDynamicFieldErrors = () => {
    setDynamicFieldErrors({});
  };

  const validateDynamicFields = (): boolean => {
    const errors: DynamicFieldErrors = {};
    let isValid = true;

    Object.entries(dynamicFields).forEach(([fieldKey, field]) => {
      const value = dynamicFieldValues[fieldKey];

      // Consider the field required only if explicitly marked as required
      const isRequired = field.is_required === true;

      // Exclude passport and personal image fields from validation
      const isImageField =
        fieldKey === "passport_image" || fieldKey === "image";

      // Exclude relationship field if user is not a companion
      const isRelationshipField = fieldKey === "relationship" && !isCompanion;

      if (field.type === "email" && value) {
        if (!isValidEmail(value as string)) {
          const label = getFieldDisplayLabel(field, fieldKey);
          errors[fieldKey] = `${label} is invalid`;
          isValid = false;
        }
      }
      if (field.type === "link" && value) {
        if (!isValidUrl(normalizeUrl(value as string))) {
          const label = getFieldDisplayLabel(field, fieldKey);
          errors[fieldKey] = `${label} is invalid`;
          isValid = false;
        }
      }
      if (isRequired && !isImageField && !isRelationshipField) {
        // Get the display label using the same logic as the UI
        const displayLabel = getFieldDisplayLabel(field, fieldKey);

        if (field.type === "multi_select") {
          if (!Array.isArray(value) || value.length === 0) {
            errors[fieldKey] = `${displayLabel} is required`;
            isValid = false;
          }
        } else {
          if (!value || (typeof value === "string" && value.trim() === "")) {
            errors[fieldKey] = `${displayLabel} is required`;
            isValid = false;
          }
        }
      }
      if (fieldKey === "first_name" || fieldKey === "last_name") {
        if (value && !/^[a-zA-Z\s\-'\.]+$/.test(value as string)) {
          errors[fieldKey] =
            `Only letters, spaces, hyphens, apostrophes, and dots are allowed in ${fieldKey.replace("_", " ")}`;
          isValid = false;
        }
      }
    });

    if (!isValid) {
      setDynamicFieldErrors(errors);
    }

    return isValid;
  };

  const getFieldNames = (): string[] => {
    return Object.keys(dynamicFields);
  };

  const getDynamicFieldKeys = (
    customPrefixValue?: string,
  ): DynamicFieldValues => {
    const valuesWithKeys: DynamicFieldValues = {};

    Object.entries(dynamicFields).forEach(([fieldKey, field]) => {
      const storedValue = dynamicFieldValues[fieldKey];

      // Handle phone number fields with object keys
      if (
        field.type === "phone_number" &&
        typeof field.key === "object" &&
        field.key.code &&
        field.key.number
      ) {
        // For phone fields, use the stored value directly for the number field
        // The country code should come from the backend state
        valuesWithKeys[field.key.number] =
          typeof storedValue === "string" ? storedValue : "";
        // Note: field.key.code will be handled separately by the component state
        return;
      }

      // Special handling for prefix field
      if (
        typeof field.key === "string" &&
        (field.key === "prefix" ||
          fieldKey === "prefix" ||
          field.renderAsPrefix)
      ) {
        // Check if "Other" is selected and we have a custom prefix value
        if (storedValue === OTHER_PREFIX_VALUE && customPrefixValue) {
          valuesWithKeys[field.key] = customPrefixValue;
        } else {
          valuesWithKeys[field.key] = storedValue || "";
        }
        return;
      }

      // Skip non-string keys for other field types (they should have been handled above)
      if (typeof field.key !== "string") {
        return;
      }

      // Only translate select-like fields (we store ids there)
      if (
        ["single_select", "multi_select", "dropdown_list"].includes(
          field.type,
        ) &&
        field.options
      ) {
        // Build normalized list with deterministic ids (indexes) matching what UI used.
        const norm = (field.options as any[]).map((opt, index) => {
          if (typeof opt === "string") {
            return { id: index.toString(), label: opt, value: opt };
          }
          return {
            id: (opt.id ?? index).toString(),
            label: opt.label,
            value: opt.value ?? opt.label,
          };
        });

        if (field.type === "multi_select" && Array.isArray(storedValue)) {
          // Map each stored id -> label
          valuesWithKeys[field.key] = storedValue
            .map((id) => {
              const found = norm.find((o) => o.id === id);
              return found ? found.label : id;
            })
            .filter(Boolean);
        } else if (typeof storedValue === "string") {
          const found = norm.find((o) => o.id === storedValue);
          valuesWithKeys[field.key] = found ? found.label : "";
        } else {
          // No selection
          valuesWithKeys[field.key] = "";
        }
      } else if (field.type === "checkbox") {
        valuesWithKeys[field.key] = storedValue || "false";
      } else if (field.type === "link" && typeof storedValue === "string") {
        // send the same normalized value that passed validation
        valuesWithKeys[field.key] = normalizeUrl(storedValue);
      } else {
        // Non select fields passed
        valuesWithKeys[field.key] = storedValue;
      }
    });

    return valuesWithKeys;
  };

  return {
    dynamicFieldValues,
    dynamicFieldErrors,
    dynamicFields,
    hasDynamicFields,
    updateDynamicField,
    setDynamicFieldError,
    clearDynamicFieldErrors,
    validateDynamicFields,
    getFieldNames,
    getDynamicFieldKeys,
  };
};

export default useDynamicFields;
